// Utility functions for fetching data from the CoinGecko API
// These wrap axios calls and return parsed JSON. They intentionally add
// simple retry/error handling so UI components can surface friendly messages.
import axios from 'axios';

const COINGECKO_BASE = 'https://api.coingecko.com/api/v3';

/**
 * Fetch detailed data for a single coin by id
 * @param {string} id coin id (e.g. 'bitcoin')
 * @returns {Promise<object>} coin data
 */
export async function fetchCoinDetails(id) {
  if (!id) throw new Error('Coin id is required');
  const { data } = await axios.get(`${COINGECKO_BASE}/coins/${id}`, {
    params: {
      localization: false,
      tickers: false,
      market_data: true,
      community_data: false,
      developer_data: false,
      sparkline: false,
    },
  });
  return data;
}

/**
 * Fetch OHLC candlestick data for a coin.
 * CoinGecko only supports 1, 7, or 30 day intervals.
 * @param {string} id  coin id
 * @param {1|7|30} days  number of days
 * @returns {Promise<Array<{time:number,open:number,high:number,low:number,close:number}>>}
 */
export async function fetchCoinOHLC(id, days = 1) {
  if (!id) throw new Error('Coin id is required');
  const allowed = [1, 7, 30];
  const safeDays = allowed.includes(days) ? days : 1;

  const { data } = await axios.get(`${COINGECKO_BASE}/coins/${id}/ohlc`, {
    params: { vs_currency: 'usd', days: safeDays },
  });

  // CoinGecko returns [ timestamp, open, high, low, close ]
  return (data || []).map(([t, o, h, l, c]) => ({
    time: Math.floor(t / 1000), // lightweight-charts expects seconds
    open: o,
    high: h,
    low: l,
    close: c,
  }));
}
